import { useCallback } from 'react'
import { TCertificationSchema } from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/schema/CertificationSchema'
import { IUseCarrierLoadPreferencesSubmitter } from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/types'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { ICertificationCode } from 'types/FMSGraphqlTypes'
import { Maybe } from 'types/graphqlTypes'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { TOAST_ERROR_MESSAGE } from '../../../constants'
import { useDeleteCertificationMutation } from '../graphql/DeleteCertificationMutation'
import { useUpdateCertificationMutation } from '../graphql/UpdateCertificationMutation'
import { getUpdateMotorCarrierCertificationInput } from '../utils/getUpdateMotorCarrierCertificationInput'

const TOAST_ADD_SUCCESS_MESSAGE = 'Certification was added successfully'
const TOAST_EDIT_SUCCESS_MESSAGE = 'Certification was updated successfully'
const TOAST_DELETE_SUCCESS_MESSAGE = 'Certification was deleted successfully'

export const useDiverseCarrierCertificationsSubmitter = (
  motorCarrierId: string,
  isEditing: boolean,
  certificationCode?: Maybe<ICertificationCode>
): IUseCarrierLoadPreferencesSubmitter<TCertificationSchema> => {
  const { openToast } = useGlobalToast()
  const { trackEvent } = useFMSAnalytics()

  const [updateCertification, { loading: updateLoading }] = useUpdateCertificationMutation()
  const [deleteCertification, { loading: deleteLoading }] = useDeleteCertificationMutation()
  const loading = updateLoading || deleteLoading

  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: TOAST_ERROR_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const submitHandler = useCallback(
    async (formValues: TCertificationSchema) => {
      const input = getUpdateMotorCarrierCertificationInput({ formValues, motorCarrierId })
      if (!input) {
        return false
      }
      try {
        const { errors } = await updateCertification({
          variables: {
            input,
          },
        })
        if (!errors) {
          trackEvent('Carrier Profile', 'UPDATED_CARRIER_PREFERENCE', {
            type: 'certification',
          })
          trackEvent('Carrier Profile', isEditing ? 'UPDATED_CERTIFICATION' : 'ADDED_CERTIFICATION')
          openToast({
            toastMessage: isEditing ? TOAST_EDIT_SUCCESS_MESSAGE : TOAST_ADD_SUCCESS_MESSAGE,
          })
          return true
        }
        showErrorToast()
        return false
      } catch {
        showErrorToast()
        return false
      }
    },
    [motorCarrierId, isEditing, openToast, showErrorToast, trackEvent, updateCertification]
  )

  const deleteHandler = useCallback(async () => {
    if (!certificationCode) {
      return false
    }
    try {
      const { errors } = await deleteCertification({
        variables: {
          input: {
            motorCarrierId,
            code: certificationCode,
          },
        },
      })
      if (!errors) {
        trackEvent('Carrier Profile', 'UPDATED_CARRIER_PREFERENCE', {
          type: 'certification',
        })
        trackEvent('Carrier Profile', 'DELETED_CERTIFICATION')
        openToast({ toastMessage: TOAST_DELETE_SUCCESS_MESSAGE })
        return true
      }
      showErrorToast()
      return false
    } catch {
      showErrorToast()
      return false
    }
  }, [
    certificationCode,
    deleteCertification,
    motorCarrierId,
    openToast,
    showErrorToast,
    trackEvent,
  ])

  return {
    loading,
    submitHandler,
    deleteHandler,
  }
}
